import { SliderValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SliderValue';
import {
    ColorPalette,
    TRANSPARENT_COLOR
} from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { useMapheimDebounced } from 'hooks/general/useMapheimDebounced';
import { useContext } from 'react';
import { IPolygonV2WorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IPolygonV2WorldMarker;
}

const BORDER_SIZE_STEPS = [0, 1, 3, 5, 7];
const FILL_OPACITY_STEPS = [0, 0.25, 0.5, 0.75, 1];

export const PolygonMarkerStyleSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);

    const { marker } = props;

    function updateBorderWidth(value: number) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            border: { ...marker.elementData.border, width: value, hasBorder: value > 0 }
        });
    }

    function updateColor(value: string) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            fill: {
                ...marker.elementData.fill,
                color: value,
                isFilled: value !== TRANSPARENT_COLOR
            },
            border: {
                ...marker.elementData.border,
                hasBorder: value !== TRANSPARENT_COLOR,
                color: value
            }
        });
    }

    function setTransparent() {
        ctxEdit.updateWorldMarkerData(marker.id, {
            fill: { ...marker.elementData.fill, isFilled: false },
            border: { ...marker.elementData.border, hasBorder: false, width: 0 }
        });
    }

    function updateFillOpacity(value: number) {
        ctxEdit.updateWorldMarkerData(marker.id, {
            fill: { ...marker.elementData.fill, opacity: value, isFilled: value > 0 }
        });
    }

    const updateBorderWidthDebounced = useMapheimDebounced(updateBorderWidth, 100);
    const updateFillOpacityDebounced = useMapheimDebounced(updateFillOpacity, 100);

    return (
        <S_PolygonMarkerStyleSection className={props.className}>
            <SidebarSection title={Localization.STYLE} icon={{ iconName: 'brush' }}>
                <SidebarSubsection title={Localization.COLOR} icon={{ iconName: 'circle' }}>
                    <SidebarContent>
                        <ColorPalette
                            value={
                                marker.elementData.fill.isFilled
                                    ? marker.elementData.fill.color
                                    : TRANSPARENT_COLOR
                            }
                            pickerAllowed
                            onChange={(color) => {
                                if (color === TRANSPARENT_COLOR) {
                                    setTransparent();
                                } else {
                                    updateColor(color);
                                }
                            }}
                            transparentAllowed
                        />
                    </SidebarContent>
                </SidebarSubsection>
                <SidebarSubsection title={Localization.BORDER_SIZE} icon={{ iconName: 'ruler' }}>
                    <SidebarContent>
                        <SliderValue
                            steps={BORDER_SIZE_STEPS}
                            value={marker.elementData.border?.width}
                            onValueChange={updateBorderWidthDebounced}
                        />
                    </SidebarContent>
                </SidebarSubsection>
                {marker?.elementData?.fill?.isFilled && (
                    <SidebarSubsection
                        title={Localization.OPACITY}
                        icon={{ iconName: 'diagram-venn' }}
                        disableBottomBorder
                    >
                        <SidebarContent>
                            <SliderValue
                                steps={FILL_OPACITY_STEPS}
                                value={marker.elementData.fill?.opacity}
                                onValueChange={updateFillOpacityDebounced}
                            />
                        </SidebarContent>
                    </SidebarSubsection>
                )}
            </SidebarSection>
        </S_PolygonMarkerStyleSection>
    );
};

const S_PolygonMarkerStyleSection = styled.div``;
